/**
 * Quiz Store - Manages quiz/MCQ state with saved challenges
 */

import { create } from 'zustand';
import { mcqApi, MCQQuestion, flashcardsApi, Flashcard, challengeTrialApi, ChallengeAccess, ChallengeTrial, ApiError } from '../services/api';

export type { MCQQuestion };

export interface QuizFlashcard {
  front: string;
  back: string;
}

export interface SavedChallenge {
  id: string;
  name: string;
  materialId: string;
  materialName: string;
  questions: MCQQuestion[];
  flashcards: QuizFlashcard[];
  createdAt: string;
}

interface QuizState {
  challengeOwner: string | null;
  challengeAccess: ChallengeAccess | null;
  challengeAccessLoading: boolean;
  challengeAccessError: boolean;
  setChallengeOwner: (owner: string | null) => void;
  refreshChallengeAccess: (owner: string, getToken: () => Promise<string | null>) => Promise<void>;
  reopenTrial: (owner: string) => boolean;

  // Saved challenges
  savedChallenges: SavedChallenge[];
  activeChallengeId: string | null;

  // Active challenge data (from the selected saved challenge)
  questions: MCQQuestion[];
  flashcards: QuizFlashcard[];

  // UI state
  isGenerating: boolean;
  generationError: string | null;
  hasGenerated: boolean;

  // Quiz progress
  currentIndex: number;
  userAnswers: Record<number, number>;
  isComplete: boolean;

  // Actions
  generateQuiz: (
    materialId: string,
    materialName: string,
    token: string,
    options: {
      ownerId: string;
      numQuestions: number;
      numFlashcards: number;
      useQuestions: boolean;
      useFlashcards: boolean;
      difficulty?: 'easy' | 'medium' | 'hard';
      type?: 'comprehension' | 'factual' | 'analytical';
    }
  ) => Promise<void>;

  loadChallenge: (id: string) => void;
  deleteChallenge: (id: string) => void;
  setCurrentIndex: (index: number) => void;
  setUserAnswer: (questionIndex: number, answer: number) => void;
  completeQuiz: () => void;
  resetQuiz: () => void;
  clearQuiz: () => void;
}

/**
 * Generate a short descriptive name for a challenge based on the questions.
 * Extracts common topic words from the first few questions.
 */
function generateChallengeName(
  materialName: string,
  questions: MCQQuestion[],
  flashcards: QuizFlashcard[]
): string {
  // Clean up material name (remove extension)
  const cleanName = materialName.replace(/\.[^/.]+$/, '');
  // Truncate if too long
  const shortName = cleanName.length > 30 ? cleanName.substring(0, 27) + '...' : cleanName;

  const parts: string[] = [];
  if (questions.length > 0) parts.push(`${questions.length}Q`);
  if (flashcards.length > 0) parts.push(`${flashcards.length}FC`);

  const date = new Date();
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return `${shortName} · ${parts.join(', ')} · ${dateStr}`;
}

// Shared by the responsive views; owner/revision checks reject late requests after account changes.
let accessRequest: { owner: string; promise: Promise<void> } | null = null;
let ownerRevision = 0;
let accessRevision = 0;
const progressReset = { currentIndex: 0, userAnswers: {}, isComplete: false };
function trialContent(trial: ChallengeTrial) {
  const c = trial.challenge!;
  return { activeChallengeId: `trial:${c.id}`, questions: c.questions, flashcards: [],
    hasGenerated: true, ...progressReset };
}

export const useQuizStore = create<QuizState>((set, get) => ({
  challengeOwner: null,
  challengeAccess: null,
  challengeAccessLoading: false,
  challengeAccessError: false,
  setChallengeOwner: (owner) => {
    if (get().challengeOwner === owner) return;
    const previous = get().challengeOwner;
    ownerRevision++;
    accessRevision++;
    accessRequest = null;
    set({ challengeOwner: owner, challengeAccess: null, challengeAccessLoading: false, challengeAccessError: false,
      isGenerating: false, generationError: null,
      ...(previous ? { savedChallenges: [], activeChallengeId: null, questions: [], flashcards: [], hasGenerated: false, ...progressReset } : {}),
    });
  },
  refreshChallengeAccess: (owner, getToken) => {
    if (get().challengeOwner !== owner) return Promise.resolve();
    if (accessRequest?.owner === owner) return accessRequest.promise;
    const revision = ++accessRevision;
    const current = () => get().challengeOwner === owner && accessRevision === revision;
    set({ challengeAccessLoading: true, challengeAccessError: false });
    const promise = (async () => {
      try {
        const token = await Promise.resolve().then(getToken);
        if (!current()) return;
        if (!token) throw new Error('Authentication required');
        const result = await challengeTrialApi.status(token);
        if (current()) set({ challengeAccess: result, challengeAccessLoading: false, challengeAccessError: false });
      } catch {
        if (current()) set({ challengeAccess: null, challengeAccessLoading: false, challengeAccessError: true });
      } finally {
        if (current()) accessRequest = null;
      }
    })();
    accessRequest = { owner, promise };
    return promise;
  },
  reopenTrial: (owner) => {
    const state = get();
    const trial = state.challengeAccess?.trial;
    if (state.challengeOwner !== owner || state.challengeAccessLoading || !trial?.challenge || trial.status !== 'completed') return false;
    set({ ...trialContent(trial), generationError: null });
    return true;
  },
  savedChallenges: [],
  activeChallengeId: null,
  questions: [],
  flashcards: [],
  isGenerating: false,
  generationError: null,
  hasGenerated: false,
  currentIndex: 0,
  userAnswers: {},
  isComplete: false,

  generateQuiz: async (materialId, materialName, token, options) => {
    const access = get().challengeAccess;
    const owner = options.ownerId;
    const invalid = (code: string) => new ApiError(403, 'Challenge access needs checking.', { code });
    if (!owner || get().challengeOwner !== owner || !access || get().challengeAccessLoading) throw invalid('CHALLENGE_UNAVAILABLE');
    if (get().isGenerating) throw invalid('CHALLENGE_TRIAL_BUSY');
    if (!access.paid && access.trial.status !== 'available') throw invalid('CHALLENGE_TRIAL_USED');
    if (!access.paid && (!options.useQuestions || options.useFlashcards)) throw invalid('CHALLENGE_PAID_REQUIRED');
    for (const [enabled, count] of [[options.useQuestions, options.numQuestions], [options.useFlashcards, options.numFlashcards]] as const) {
      if (enabled && (!Number.isInteger(count) || count < 1 || count > access.maxQuestions)) throw invalid('CHALLENGE_INVALID_INPUT');
    }
    const revision = ownerRevision;
    const current = () => get().challengeOwner === owner && ownerRevision === revision;
    accessRevision++;
    accessRequest = null;
    set({ isGenerating: true, generationError: null, challengeAccessLoading: false });

    try {
      let completedTrial: ChallengeTrial | undefined;
      const results: { questions: MCQQuestion[]; flashcards: QuizFlashcard[] } = {
        questions: [],
        flashcards: [],
      };

      // Generate MCQ questions if enabled and count > 0
      if (options.useQuestions && options.numQuestions > 0) {
        const mcqResponse = await mcqApi.generate(materialId, token, {
          quantity: options.numQuestions,
          difficulty: options.difficulty,
          type: options.type,
        });
        if (!current()) return;
        if (!mcqResponse.success) throw invalid('CHALLENGE_GENERATION_FAILED');
        completedTrial = mcqResponse.trial;
        results.questions = mcqResponse.questions || [];
        if (!access.paid && (!completedTrial?.challenge || completedTrial.status !== 'completed')) throw invalid('CHALLENGE_UNAVAILABLE');
      }

      // Generate flashcards if enabled and count > 0
      if (options.useFlashcards && options.numFlashcards > 0) {
        try {
          const flashcardResponse = await flashcardsApi.generateFlashcards(
            materialId,
            options.numFlashcards,
            token
          );
          if (!current()) return;
          results.flashcards = (flashcardResponse.flashcards || []).map((fc: Flashcard) => ({
            front: fc.front,
            back: fc.back,
          }));
        } catch (err) {
          if (!current()) return;
          if (err instanceof ApiError && err.status === 403) throw err;
          console.warn('Flashcard generation failed, continuing with questions only:', err);
        }
      }
      if (!current()) return;
      if (completedTrial?.challenge) {
        accessRevision++;
        accessRequest = null;
        set({ ...trialContent(completedTrial), isGenerating: false, challengeAccessLoading: false,
          challengeAccess: { ...access, trial: completedTrial } });
        return;
      }

      if (results.questions.length === 0 && results.flashcards.length === 0) {
        throw new Error('No quiz content could be generated. Please try again.');
      }

      // Create saved challenge
      const challengeId = `challenge_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      const challengeName = generateChallengeName(materialName, results.questions, results.flashcards);

      const newChallenge: SavedChallenge = {
        id: challengeId,
        name: challengeName,
        materialId,
        materialName,
        questions: results.questions,
        flashcards: results.flashcards,
        createdAt: new Date().toISOString(),
      };

      set((state) => ({
        savedChallenges: [newChallenge, ...state.savedChallenges],
        activeChallengeId: challengeId,
        questions: results.questions,
        flashcards: results.flashcards,
        isGenerating: false,
        hasGenerated: true,
        currentIndex: 0,
        userAnswers: {},
        isComplete: false,
      }));
    } catch (error: any) {
      if (!current()) return;
      set({
        isGenerating: false,
        generationError: error?.data?.code || 'CHALLENGE_GENERATION_FAILED',
      });
      throw error;
    }
  },

  loadChallenge: (id: string) => {
    const challenge = get().savedChallenges.find((c) => c.id === id);
    if (challenge) {
      set({
        activeChallengeId: id,
        questions: challenge.questions,
        flashcards: challenge.flashcards,
        hasGenerated: true,
        currentIndex: 0,
        userAnswers: {},
        isComplete: false,
      });
    }
  },

  deleteChallenge: (id: string) => {
    set((state) => {
      const newChallenges = state.savedChallenges.filter((c) => c.id !== id);
      // If we deleted the active one, clear active state
      if (state.activeChallengeId === id) {
        return {
          savedChallenges: newChallenges,
          activeChallengeId: null,
          questions: [],
          flashcards: [],
          hasGenerated: false,
          currentIndex: 0,
          userAnswers: {},
          isComplete: false,
        };
      }
      return { savedChallenges: newChallenges };
    });
  },

  setCurrentIndex: (index) => set({ currentIndex: index }),

  setUserAnswer: (questionIndex, answer) => {
    set((state) => ({
      userAnswers: { ...state.userAnswers, [questionIndex]: answer },
    }));
  },

  completeQuiz: () => set({ isComplete: true }),

  resetQuiz: () =>
    set({
      currentIndex: 0,
      userAnswers: {},
      isComplete: false,
    }),

  clearQuiz: () =>
    set({
      activeChallengeId: null,
      questions: [],
      flashcards: [],
      hasGenerated: false,
      generationError: null,
      currentIndex: 0,
      userAnswers: {},
      isComplete: false,
    }),
}));
